import { Dispatch, SetStateAction, useMemo } from 'react';
import { Stack } from '@optimus-web/core';
import { AdditionalSearchType, AssistantSearchType, SearchSubSection, SearchType } from '../../assistantSearchTypes';
import { ListTitle } from '../results/ListTitle';
import { ThinDivider } from '../styles';
import { ShowMoreOrLessStateProps } from '../../Assistant';
import { AssistantSearchResultListItem } from '../components/AssistantSearchResultListItem';
import { AssistantSearchResultListShowMoreButton } from '../components/AssistantSearchResultListShowMoreButton';
import { AssistantSearchListItemProps } from './useAssistantSearchResultListMap';

interface SearchResultListComponentProps {
    selectedSearchType: AssistantSearchType;
    assistantSearchResultListMap: Record<SearchType | SearchSubSection, AssistantSearchListItemProps[]>;
    showAll: boolean;
    setHoveredResultType: Dispatch<SetStateAction<AssistantSearchType | undefined>>;
    setShowMoreOrLessState: Dispatch<SetStateAction<ShowMoreOrLessStateProps>>;
    showMoreOrLessState: ShowMoreOrLessStateProps;
    handleUnFocused: () => void;
    chatPanelSearch: boolean;
    isTabNavigation: boolean;
    searchResultList: AssistantSearchListItemProps[];
    selectedIndex: number;
}

export const useSearchResultListComponent = ({
    selectedSearchType,
    assistantSearchResultListMap,
    showAll,
    setHoveredResultType,
    setShowMoreOrLessState,
    showMoreOrLessState,
    handleUnFocused,
    isTabNavigation,
    chatPanelSearch,
    searchResultList,
    selectedIndex,
}: SearchResultListComponentProps) => {
    const { itemsToShow, initialItemsToShow } = showMoreOrLessState;

    return useMemo(() => {
        let initialIndex = 0;

        const getHoveredSearchType = (searchType: AssistantSearchType | undefined) => {
            setHoveredResultType(searchType);
        };

        const generateList = (
            searchType: AssistantSearchType,
            list: Array<AssistantSearchListItemProps>,
            showButtons: boolean,
            index?: number
        ) => {
            const handleShowMore = (type: SearchType) => {
                setShowMoreOrLessState((prev) => ({
                    ...prev,
                    itemsToShow: {
                        ...prev.itemsToShow,
                        [type]: list.length,
                    },
                }));
            };
            const handleShowLess = (type: SearchType) => {
                setShowMoreOrLessState((prev) => ({
                    ...prev,
                    itemsToShow: {
                        ...prev.itemsToShow,
                        [type]: initialItemsToShow[type],
                    },
                }));
            };

            const getResultList = (resultList: Array<AssistantSearchListItemProps>) =>
                resultList.map((listItemProps) => {
                    initialIndex++;
                    const isSelected = !isTabNavigation && selectedIndex === initialIndex;
                    return (
                        <AssistantSearchResultListItem
                            key={listItemProps.itemId}
                            assistantSearchListItemProps={listItemProps}
                            focused={isSelected}
                            chatPanelSearch={chatPanelSearch}
                            searchType={searchType}
                            handleUnFocused={handleUnFocused}
                            data-test-id={listItemProps['data-test-id']}
                        />
                    );
                });

            const slicedList = showButtons ? list.slice(0, itemsToShow[searchType]) : list;

            return list.length > 0 ? (
                <Stack spacing="100" vertical key={searchType}>
                    {index ? <ThinDivider /> : null}
                    <Stack
                        vertical
                        spacing={chatPanelSearch ? '100' : '50'}
                        className="stack-list"
                        onMouseEnter={() => getHoveredSearchType(searchType)}
                        onMouseLeave={() => getHoveredSearchType(undefined)}
                    >
                        {showAll ? <ListTitle searchType={searchType} hideDivider /> : null}
                        {getResultList(slicedList)}
                    </Stack>
                    {showButtons && list.length > initialItemsToShow[searchType] && (
                        <AssistantSearchResultListShowMoreButton
                            searchType={searchType}
                            isShowMore={itemsToShow[searchType] < list.length}
                            searchListLength={searchResultList.length}
                            handleShowMore={handleShowMore}
                            handleShowLess={handleShowLess}
                        />
                    )}
                </Stack>
            ) : null;
        };

        return (
            <Stack vertical spacing="100">
                {selectedSearchType === AdditionalSearchType.All
                    ? Object.entries(assistantSearchResultListMap)
                          .filter(([_key, value]) => value && value.length > 0)
                          .map(([key, value], index) => generateList(key as SearchType, value, true, index))
                    : generateList(selectedSearchType, assistantSearchResultListMap[selectedSearchType], false)}
            </Stack>
        );
    }, [
        assistantSearchResultListMap,
        chatPanelSearch,
        handleUnFocused,
        initialItemsToShow,
        isTabNavigation,
        itemsToShow,
        searchResultList.length,
        selectedIndex,
        selectedSearchType,
        setHoveredResultType,
        setShowMoreOrLessState,
        showAll,
    ]);
};
